import { curry, partition, uniq } from 'ramda'; // TODO: Migrate to remedajs

export enum TicketEventType {
  CREATE_TICKET = 'CREATE_TICKET',
  UPDATE_TICKET = 'UPDATE_TICKET',
  DELETE_TICKET = 'DELETE_TICKET',
  ADD_MESSAGE = 'ADD_MESSAGE',
  ADD_LOCATION = 'ADD_LOCATION',
  ADD_ATTACHMENT = 'ADD_ATTACHMENT',
  UPDATE_MESSAGE = 'UPDATE_MESSAGE',
  UPDATE_LOCATION = 'UPDATE_LOCATION',
  DELETE_MESSAGE = 'DELETE_MESSAGE',
  DELETE_LOCATION = 'DELETE_LOCATION',
  DELETE_ATTACHMENT = 'DELETE_ATTACHMENT',
  NOTIFICATION_OPT_IN = 'NOTIFICATION_OPT_IN',
  NOTIFICATION_OPT_OUT = 'NOTIFICATION_OPT_OUT',
}

export enum TicketStatus {
  OPENED = 'opened',
  CLOSED = 'closed',
}

interface Coordinates {
  accuracy: number;
  altitude: number | null;
  altitudeAccuracy: number | null;
  heading: number | null;
  latitude: number;
  longitude: number;
  speed: number | null;
}

interface Position {
  coords?: Coordinates;
  timestamp?: number;
}

export type Meta = {
  originTicketId?: string;
  originListId?: string;
  originMeetingId?: string;
  /** @deprecated */ meetingId?: string;
  referencedTicketIds?: string[];
  referencedUserIds?: number[];
  referencedListIds?: string[];
};

// TODO: avoid Date|string and create DTO types using a helper type

// TODO: create a better, composite type; which conveys which fields are mandatory for which each event type
export interface TicketEvent {
  id: string;
  recnum?: number;
  ticket_id: string;
  created: Date | string;
  inserted?: Date | string;
  user_id: number;
  payload: {
    assignee_user_id?: number | null;
    executor_user_id?: number | null;
    title?: string;
    description?: string;
    status?: TicketStatus;
    werk_id?: number | null;
    project_id?: number | null;
    due?: null | Date | string;
    eventId?: string;
    message?: string;
    label?: string;
    add_labels?: number[];
    remove_labels?: number[];
    size?: number;
    checksum?: string;
    contentType?: string;
    /** @deprecated */
    add_tags?: string[];
    /** @deprecated */
    remove_tags?: string[];
  } & Position;
  meta: Meta | null;
  type: TicketEventType;
}

export interface Ticket {
  id: string;
  recnum?: number;
  number: number;
  title: string;
  description: string;
  created: Date | string;
  creator_user_id: number;
  modified: Date | string;
  assignee_user_id: number | null;
  executor_user_id: number | null;
  werk_id: number | null;
  project_id: number | null;
  status: TicketStatus;
  due: null | Date | string;
  /** @deprecated */
  tags: string[];
  labels: number[];
  events: TicketEvent[];
  /** users who have  im- or explicitly opted in for notifications */
  notification_opt_in: number[];
  /** users who have explicitly opted out of notifications */
  notification_opt_out: number[];
}

export const isUserInvolvedWithTicket = curry(
  (userId: number, ticket: Ticket) => {
    if (
      ticket.assignee_user_id === userId ||
      ticket.creator_user_id === userId ||
      ticket.executor_user_id === userId
    ) {
      return true;
    }
    for (const event of ticket.events) {
      if (
        event.user_id === userId ||
        event.payload?.executor_user_id === userId ||
        event.payload?.assignee_user_id === userId
      ) {
        return true;
      }
    }
    return false;
  },
);

export function getTicketUsers(ticket: { events: TicketEvent[] }): number[] {
  const assignedRoles: number[] = [];
  const userIds: number[] = ticket.events.map((event) => {
    if (event.payload && event.payload.assignee_user_id) {
      assignedRoles.push(event.payload.assignee_user_id);
    }
    if (event.payload && event.payload.executor_user_id) {
      assignedRoles.push(event.payload.executor_user_id);
    }
    return event.user_id;
  });
  return uniq([...userIds, ...assignedRoles]);
}

const editTicketEventTypes: TicketEventType[] = [
  TicketEventType.UPDATE_MESSAGE,
  TicketEventType.UPDATE_LOCATION,
];
const deleteTicketEventTypes: TicketEventType[] = [
  TicketEventType.DELETE_MESSAGE,
  TicketEventType.DELETE_LOCATION,
  TicketEventType.DELETE_ATTACHMENT,
];

/**
 * editAndDeleteTicketEventTypes
 *
 * to clarify:
 *   These event target other events and delete/modify them. i.e. editing/deleting a attachment/message
 *   This does not include updating fields of the ticket
 */
const editAndDeleteTicketEventTypes: TicketEventType[] = [
  ...editTicketEventTypes,
  ...deleteTicketEventTypes,
];

/**
 * This function takes a (sub)set of raw ticket-events and reduces them to a relevant set of events.
 * This is useful for describing an events where message/image-corrections/deletions etc. are not relevant to consider separately and should be reduced to a single event or no-op
 *
 * NB when processing a subset and a deletion/edit target can not be found; it is omitted.
 */
export function processEditAndDeleteTicketEvents(events: TicketEvent[]) {
  const [eadEvents, displayEvents] = partition(
    (e) => editAndDeleteTicketEventTypes.includes(e.type),
    events,
  );

  const [deleteEvents, editEvents] = partition(
    (e) => deleteTicketEventTypes.includes(e.type),
    eadEvents,
  );

  const deletedEventIds = deleteEvents.map((e) => e.payload.eventId);

  return displayEvents
    .filter((e) => !deletedEventIds.includes(e.id))
    .map((displayEvent) => {
      return editEvents.reduce((processedDisplayEvent, editEvent) => {
        const { eventId, ...update } = editEvent.payload;
        if (eventId === processedDisplayEvent.id) {
          return {
            ...processedDisplayEvent,
            payload: { ...processedDisplayEvent.payload, ...update },
          };
        }
        return processedDisplayEvent;
      }, displayEvent);
    });
}
